import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const VacationManagement = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get('/admin/vacation/requests');
      setRequests(res.data);
    } catch (error) {
      console.error('Error loading vacation requests:', error);
      toast.error('Fehler beim Laden der Anträge');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Möchten Sie diesen Urlaubsantrag genehmigen?')) return;

    try {
      await axios.put(`/admin/vacation/${requestId}/approve`);
      toast.success('Urlaubsantrag genehmigt');
      loadRequests();
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      toast.error('Bitte geben Sie einen Grund für die Ablehnung an');
      return;
    }

    try {
      await axios.put(`/admin/vacation/${requestId}/reject`, {
        reason: rejectionReason
      });
      toast.success('Urlaubsantrag abgelehnt');
      setRejectingId(null);
      setRejectionReason('');
      loadRequests();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, color: 'bg-yellow-900/30 text-yellow-400 border-yellow-700', label: 'Ausstehend' },
      approved: { icon: CheckCircle, color: 'bg-green-900/30 text-green-400 border-green-700', label: 'Genehmigt' },
      rejected: { icon: XCircle, color: 'bg-red-900/30 text-red-400 border-red-700', label: 'Abgelehnt' }
    };
    const { icon: Icon, color, label } = config[status] || config.pending;
    return (
      <Badge className={`${color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(req => 
    filter === 'all' || req.status === filter
  );

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-4xl font-bold text-white">Urlaubsverwaltung</h1>
        <p className="text-zinc-400 mt-2">Urlaubsanträge genehmigen oder ablehnen</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-zinc-700" onClick={() => setFilter('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-sm text-zinc-400">Gesamt</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-900/20 border-yellow-700 cursor-pointer hover:border-yellow-600" onClick={() => setFilter('pending')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            <p className="text-sm text-zinc-400">Ausstehend</p>
          </CardContent>
        </Card>
        <Card className="bg-green-900/20 border-green-700 cursor-pointer hover:border-green-600" onClick={() => setFilter('approved')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
            <p className="text-sm text-zinc-400">Genehmigt</p>
          </CardContent>
        </Card>
        <Card className="bg-red-900/20 border-red-700 cursor-pointer hover:border-red-600" onClick={() => setFilter('rejected')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
            <p className="text-sm text-zinc-400">Abgelehnt</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Indicator */}
      {filter !== 'all' && (
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
            Filter: {filter === 'pending' ? 'Ausstehend' : filter === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
            Filter zurücksetzen
          </Button>
        </div>
      )}

      {/* Requests List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Urlaubsanträge ({filteredRequests.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">Keine Anträge gefunden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map(request => (
                <div key={request.request_id} className="p-4 bg-zinc-800 rounded-lg space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="h-12 w-12 rounded-full bg-blue-900/30 flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-white text-lg">{request.user_name}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <p className="text-sm text-zinc-400">
                            📅 {new Date(request.start_date).toLocaleDateString('de-DE')} - {new Date(request.end_date).toLocaleDateString('de-DE')}
                          </p>
                          <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                            {request.days_requested} Tag(e)
                          </Badge>
                        </div>
                        {request.reason && (
                          <p className="text-sm text-zinc-300 mt-2">
                            💬 {request.reason}
                          </p>
                        )}
                        {request.rejection_reason && (
                          <p className="text-sm text-red-400 mt-2">
                            ❌ Grund der Ablehnung: {request.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(request.status)}
                      <p className="text-xs text-zinc-500">
                        Eingereicht: {new Date(request.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {request.status === 'pending' && (
                    <div className="flex gap-2 pt-3 border-t border-zinc-700">
                      {rejectingId === request.request_id ? (
                        <div className="flex-1 space-y-2">
                          <Textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Grund für die Ablehnung..."
                            className="bg-zinc-950 border-zinc-700 text-white"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleReject(request.request_id)}
                            >
                              Ablehnen bestätigen
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRejectingId(null);
                                setRejectionReason('');
                              }}
                            >
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleApprove(request.request_id)}
                            className="flex-1"
                            size="sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Genehmigen
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => setRejectingId(request.request_id)}
                            className="flex-1"
                            size="sm"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Ablehnen
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VacationManagement;
